import { useState } from 'react';
import Col from 'react-bootstrap/Col';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import { changePassword } from '../../../authentication/cognito';
import Button from 'react-bootstrap/Button';
import Collapse from 'react-bootstrap/Collapse';
import { ButtonType, ButtonVariant, FeedbackType, FormCheckType, FormControlType, PasswordPeeker, Variant } from '../../../components';
import { PageEvent } from '../../../app-page-events';
import Container from 'react-bootstrap/Container';
import * as formik from 'formik';
import * as yup from 'yup';

import '../profile.scss';

export default function ProfilePassword({ onNotifyEvent, onPageUpdateEvent }) {

    const [changePasswordVisible, setChangePasswordVisible] = useState(false);
    const [initialPasswordValues, setInitialPasswordValues] = useState({});

    const [changePasswordSubmitButtonEnabled, setChangePasswordSubmitButtonEnabled] = useState(true);
    const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [confirmNewPasswordVisible, setConfirmNewPasswordVisible] = useState(false);

    const handleChangePasswordCollapse = (checked) => {
        setInitialPasswordValues({
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        });

        setChangePasswordVisible(checked);
    }

    const handleChangePasswordSubmit = (e) => {
        setChangePasswordSubmitButtonEnabled(false);

        changePassword(e.currentPassword, e.newPassword).then((result) => {
            setChangePasswordSubmitButtonEnabled(true);
            setChangePasswordVisible(false);

            onNotifyEvent({
                email: e.email,
                notificationTitle: 'Success',
                notificationMessage: result,
                notificationType: Variant.SUCCESS,
            });

            onPageUpdateEvent(PageEvent.PROFILE_PASSWORD_UPDATED);
        }).catch((error) => {
            setChangePasswordSubmitButtonEnabled(true);
            onNotifyEvent({
                notificationTitle: 'Error',
                notificationMessage: error,
                notificationType: Variant.DANGER,
            });
        });
    }

    const onCurrentPasswordPeekerChanged = (e) => {
        setCurrentPasswordVisible(e.passwordVisible);
    }

    const onNewPasswordPeekerChanged = (e) => {
        setNewPasswordVisible(e.passwordVisible);
    }

    const onConfirmNewPasswordPeekerChanged = (e) => {
        setConfirmNewPasswordVisible(e.passwordVisible);
    }

    const passwordSchema = yup.object().shape({
        currentPassword: yup.string().min(6, 'Password must be at least 6 characters').required('Current password is required'),
        newPassword: yup.string().min(6, 'Password must be at least 6 characters').required('New password is required'),
        confirmNewPassword: yup.string().required('Confirm new password required').oneOf([yup.ref('newPassword')], 'New Passwords must match'),
    });

    const { Formik } = formik;

    return (
        <>
            <h4>Password</h4>
            <Formik
                className='app-form-group'
                validationSchema={passwordSchema}
                initialValues={initialPasswordValues}
                enableReinitialize={true} // Reinitialize form when initialValues change
                onSubmit={handleChangePasswordSubmit}
            >
                {({ handleSubmit, handleChange, values, touched, errors }) => (
                    <Form
                        className='app-form-group'
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <Form.Check
                            className='app-change-password-check'
                            type={FormCheckType.SWITCH}
                            label={'Change password'}
                            onClick={(e) => handleChangePasswordCollapse(e.target.checked)}
                            checked={changePasswordVisible}
                        />
                        <Collapse in={changePasswordVisible}>
                            <Container>
                                <Form.Group as={Col} size='sm'>
                                    <FloatingLabel
                                        label='Current password'
                                        className='mb-3'
                                    >
                                        <Form.Control
                                            type={currentPasswordVisible ? FormControlType.TEXT : FormControlType.PASSWORD}
                                            className='app-password-input-box'
                                            name='currentPassword'
                                            placeholder='Current password'
                                            value={values.currentPassword}
                                            onChange={handleChange}
                                            isValid={touched.currentPassword && !errors.currentPassword}
                                            isInvalid={touched.currentPassword && !!errors.currentPassword}
                                        />
                                        <PasswordPeeker onChange={onCurrentPasswordPeekerChanged} passwordVisible={currentPasswordVisible} />
                                        <Form.Control.Feedback></Form.Control.Feedback>
                                        <Form.Control.Feedback type={FeedbackType.INVALID}>{errors.currentPassword}</Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>

                                <Form.Group as={Col} size='sm'>
                                    <FloatingLabel
                                        label='New password'
                                        className='mb-3'
                                    >
                                        <Form.Control
                                            type={newPasswordVisible ? FormControlType.TEXT : FormControlType.PASSWORD}
                                            className='app-password-input-box'
                                            name='newPassword'
                                            placeholder='New password'
                                            value={values.newPassword}
                                            onChange={handleChange}
                                            isValid={touched.newPassword && !errors.newPassword}
                                            isInvalid={touched.newPassword && !!errors.newPassword}
                                        />
                                        <PasswordPeeker onChange={onNewPasswordPeekerChanged} passwordVisible={newPasswordVisible} />
                                        <Form.Control.Feedback></Form.Control.Feedback>
                                        <Form.Control.Feedback type={FeedbackType.INVALID}>{errors.newPassword}</Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>

                                <Form.Group as={Col} size='sm'>
                                    <FloatingLabel
                                        label='Confirm new password'
                                        className='mb-3'
                                    >
                                        <Form.Control
                                            type={confirmNewPasswordVisible ? FormControlType.TEXT : FormControlType.PASSWORD}
                                            className='app-password-input-box'
                                            name='confirmNewPassword'
                                            placeholder='Confirm new password'
                                            value={values.confirmNewPassword}
                                            onChange={handleChange}
                                            isValid={touched.confirmNewPassword && !errors.confirmNewPassword}
                                            isInvalid={touched.confirmNewPassword && !!errors.confirmNewPassword}
                                        />
                                        <PasswordPeeker onChange={onConfirmNewPasswordPeekerChanged} passwordVisible={confirmNewPasswordVisible} />
                                        <Form.Control.Feedback></Form.Control.Feedback>
                                        <Form.Control.Feedback type={FeedbackType.INVALID}>{errors.confirmNewPassword}</Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>

                                <Button
                                    type={ButtonType.SUBMIT}
                                    variant={ButtonVariant.OUTLINE_PRIMARY}
                                    disabled={!changePasswordSubmitButtonEnabled}
                                >
                                    Change Password
                                </Button>
                            </Container>
                        </Collapse>
                    </Form>
                )}
            </Formik>
        </>
    );
}